import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Shield, Save, RotateCcw, Loader2, Clock, AlertTriangle, Lock as LockIcon } from "lucide-react";
import { authFetch } from "../../App";

const API = "/api";

export default function VaultPinSettings() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/vault/pin/config`);
      if (res.ok) {
        const d = await res.json();
        setCfg(d);
        setInitial({
          session_ttl_minutes: d.session_ttl_minutes,
          max_fail_attempts: d.max_fail_attempts,
          lock_minutes: d.lock_minutes,
        });
      } else {
        toast.error("Konfiguration konnte nicht geladen werden");
      }
    } catch {
      toast.error("Netzwerkfehler");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => {
    setCfg((c) => ({ ...c, [key]: value === "" ? "" : Number(value) }));
  };

  const dirty = initial && cfg && (
    Number(cfg.session_ttl_minutes) !== initial.session_ttl_minutes ||
    Number(cfg.max_fail_attempts) !== initial.max_fail_attempts ||
    Number(cfg.lock_minutes) !== initial.lock_minutes
  );

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/vault/pin/config`, {
        method: "PUT",
        body: JSON.stringify({
          session_ttl_minutes: Number(cfg.session_ttl_minutes),
          max_fail_attempts: Number(cfg.max_fail_attempts),
          lock_minutes: Number(cfg.lock_minutes),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Gespeichert");
        setInitial({
          session_ttl_minutes: d.session_ttl_minutes,
          max_fail_attempts: d.max_fail_attempts,
          lock_minutes: d.lock_minutes,
        });
      } else {
        toast.error(d.detail || "Speichern fehlgeschlagen");
      }
    } catch {
      toast.error("Netzwerkfehler");
    }
    setSaving(false);
  };

  const resetDefaults = () => {
    if (!cfg?.defaults) return;
    setCfg((c) => ({ ...c, ...c.defaults }));
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm" style={{ color: "var(--tga-text-muted)" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Lade…
      </div>
    );
  }

  const bounds = cfg.bounds || {};

  return (
    <div className="rounded-xl border p-5 space-y-5" style={{ backgroundColor: "var(--tga-bg-secondary)", borderColor: "var(--tga-border)" }} data-testid="vault-pin-settings">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <Shield className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-bold" style={{ color: "var(--tga-text-primary)" }}>Passwort-Tresor PIN</h2>
          <p className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>Wann wird der Tresor automatisch gesperrt? Wie oft darf man sich vertippen?</p>
        </div>
      </div>

      <Field
        icon={<Clock className="w-4 h-4 text-blue-400" />}
        label="Auto-Sperre nach Inaktivität"
        unit="Minuten"
        value={cfg.session_ttl_minutes}
        onChange={(v) => setField("session_ttl_minutes", v)}
        min={bounds.session_ttl_minutes?.min ?? 1}
        max={bounds.session_ttl_minutes?.max ?? 1440}
        testid="vault-pin-cfg-ttl"
        hint={`Nach dieser Zeit wird der entsperrte Tresor automatisch wieder gesperrt (Standard: ${cfg.defaults?.session_ttl_minutes ?? 30} Min).`}
      />

      <Field
        icon={<AlertTriangle className="w-4 h-4 text-orange-400" />}
        label="Max. Fehlversuche"
        unit="Versuche"
        value={cfg.max_fail_attempts}
        onChange={(v) => setField("max_fail_attempts", v)}
        min={bounds.max_fail_attempts?.min ?? 1}
        max={bounds.max_fail_attempts?.max ?? 20}
        testid="vault-pin-cfg-fail"
        hint={`Nach so vielen falschen PIN-Eingaben wird der Tresor komplett gesperrt (Standard: ${cfg.defaults?.max_fail_attempts ?? 5}).`}
      />

      <Field
        icon={<LockIcon className="w-4 h-4 text-red-400" />}
        label="Sperrzeit bei zu vielen Fehlversuchen"
        unit="Minuten"
        value={cfg.lock_minutes}
        onChange={(v) => setField("lock_minutes", v)}
        min={bounds.lock_minutes?.min ?? 1}
        max={bounds.lock_minutes?.max ?? 1440}
        testid="vault-pin-cfg-lock"
        hint={`Wie lange der Tresor nach Überschreiten der Fehlversuche gesperrt bleibt (Standard: ${cfg.defaults?.lock_minutes ?? 15} Min).`}
      />

      <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--tga-border)" }}>
        <button
          onClick={resetDefaults}
          disabled={saving}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg hover:bg-white/5"
          style={{ color: "var(--tga-text-muted)" }}
          data-testid="vault-pin-cfg-reset"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Standardwerte
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="vault-pin-cfg-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {dirty ? "Speichern" : "Gespeichert"}
        </button>
      </div>
    </div>
  );
}

function Field({ icon, label, unit, value, onChange, min, max, testid, hint }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--tga-text-secondary)" }}>
        {icon} {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 px-3 py-2 rounded-lg outline-none border text-sm font-mono"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", color: "var(--tga-text-primary)", borderColor: "var(--tga-border)" }}
          data-testid={testid}
        />
        <span className="text-xs" style={{ color: "var(--tga-text-muted)" }}>{unit}</span>
        <span className="text-[10px] ml-2" style={{ color: "var(--tga-text-muted)" }}>({min}–{max})</span>
      </div>
      {hint && <p className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>{hint}</p>}
    </div>
  );
}
